import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  generateSlotDrafts,
  type AvailabilityRuleUI,
  type GeneratedSlotDraft,
} from '@helios/data-access';

// ─── Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  display: 'block',
  marginBottom: 'var(--sp-2)',
};

const inputStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

// ─── Props ───

export interface GenerateSlotsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  rule: AvailabilityRuleUI | null;
  /** Returns the count of existing matching (start_time/end_time) slot keys for skip logic. */
  existingKeyCount: (drafts: GeneratedSlotDraft[]) => number;
  onGenerate: (input: {
    drafts: GeneratedSlotDraft[];
    skipExisting: boolean;
  }) => Promise<{ created: number; skipped: number } | void>;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function GenerateSlotsModal({
  open,
  onOpenChange,
  brandTokens,
  rule,
  existingKeyCount,
  onGenerate,
}: GenerateSlotsModalProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [skipExisting, setSkipExisting] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setStartDate(todayStr());
    setEndDate(rule ? addDaysIso(todayStr(), rule.generateDaysAhead) : addDaysIso(todayStr(), 60));
  }, [open, rule]);

  const drafts = useMemo(() => {
    if (!rule || !startDate || !endDate) return [];
    return generateSlotDrafts({ rule, startDate, endDate });
  }, [rule, startDate, endDate]);

  const skippedCount = useMemo(() => {
    if (!skipExisting || drafts.length === 0) return 0;
    return existingKeyCount(drafts);
  }, [drafts, skipExisting, existingKeyCount]);

  const toCreate = Math.max(drafts.length - skippedCount, 0);

  const handleGenerate = async () => {
    if (!rule) return;
    setGenerating(true);
    try {
      const res = await onGenerate({ drafts, skipExisting });
      if (res) {
        setResult(
          `Created ${res.created} slot${res.created === 1 ? '' : 's'}, skipped ${res.skipped}.`,
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Generate Slots"
      description={rule ? `For "${rule.name}"` : undefined}
      maxWidth={520}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
          <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleGenerate}
            disabled={generating || !rule || toCreate === 0}
          >
            {generating ? 'Generating...' : `Generate ${toCreate} Slot${toCreate === 1 ? '' : 's'}`}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <input
            type="checkbox"
            checked={skipExisting}
            onChange={(e) => setSkipExisting(e.target.checked)}
          />
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
            Skip existing slots
          </span>
        </label>

        <div
          style={{
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            rowGap: 'var(--sp-1)',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>Candidate slots</span>
          <span style={{ fontWeight: 'var(--font-weight-semi)' }}>{drafts.length}</span>
          <span style={{ color: 'var(--text-muted)' }}>Skipped (already exist)</span>
          <span style={{ fontWeight: 'var(--font-weight-semi)' }}>{skippedCount}</span>
          <span style={{ color: 'var(--text-muted)' }}>To create</span>
          <span style={{ fontWeight: 'var(--font-weight-semi)' }}>{toCreate}</span>
        </div>

        {result && (
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: '#dcfce7',
              color: '#15803d',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {result}
          </div>
        )}
      </div>
    </Modal>
  );
}
